/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { ArrowLeft, Envelope } from "@phosphor-icons/react";

const SECTION = "mt-8 first:mt-0";
const H2 = "font-serif-display text-2xl sm:text-3xl text-[#3F2D1B] mb-3 leading-tight";
const P = "text-sm sm:text-base text-[#3F2D1B] leading-relaxed mb-3";
const UL = "list-disc pl-6 space-y-1.5 mb-4 text-sm sm:text-base text-[#3F2D1B]";

const SUPPORT_EMAIL = "astrologykuber@gmail.com";

export default function RefundPolicyPage() {
  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-5 sm:px-8 py-10 sm:py-14" data-testid="refund-page">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted hover:text-[#3F2D1B] mb-6" data-testid="refund-back">
          <ArrowLeft size={14} /> Back to home
        </Link>

        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-700 font-semibold mb-2">
          AstroKuberChat &middot; Policies
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#3F2D1B] leading-tight">
          Cancellation &amp; Refund Policy
        </h1>
        <p className="text-xs text-muted mt-3">Effective date: 21 August 2026 &middot; Last updated: 21 August 2026</p>

        <div className="mt-10 space-y-1" data-testid="refund-content">

          <section className={SECTION}>
            <h2 className={H2}>1. Scope</h2>
            <p className={P}>
              This policy explains when and how you can cancel an order or a
              consultation booked through <b>astrokuberchat.com</b>, and how
              refunds are processed. It covers three product categories:
            </p>
            <ul className={UL}>
              <li><b>Digital products</b> — PDF reports, tool unlocks, wallet top-ups, VIP subscriptions, chat / call minutes with astrologers.</li>
              <li><b>Physical products</b> — gemstones and any other items shipped by the AstroKuberChat Store.</li>
              <li><b>1:1 consultations</b> — scheduled call / video sessions with human astrologers.</li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>2. Digital Products (Reports, Unlocks, Wallet, Chat Minutes)</h2>
            <ul className={UL}>
              <li><b>Before delivery:</b> If a report or tool unlock has been paid for but has not yet been generated or accessed, you may request a full refund within 24 hours of purchase.</li>
              <li><b>After delivery:</b> Once a PDF report has been generated / downloaded, or a chat / call session has been consumed, the purchase is considered fulfilled and is <b>non-refundable</b>. This is because the underlying AI compute and astrologer time have already been used.</li>
              <li><b>Wallet balance:</b> Money added to your AstroKuberChat wallet is refundable to the original payment method within 7 days of top-up, as long as no consultation minutes have been spent from it.</li>
              <li><b>Failed transactions:</b> If Razorpay debited your account but the credit did not appear in your wallet / your unlock did not activate, the amount is automatically refunded within 5–7 business days. If it doesn't arrive, contact us and we will trace and refund.</li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>3. Physical Products (Gemstones from AstroKuberChat Store)</h2>
            <ul className={UL}>
              <li><b>Cancellation window:</b> You can cancel a gemstone order for a full refund <b>any time before it is dispatched</b> from our warehouse. Once tracking has been generated and the parcel has been handed over to the courier, the order cannot be cancelled.</li>
              <li><b>Return / exchange window:</b> You have <b>7 days from delivery</b> to request a return or exchange, provided the stone, its packaging, and the accompanying government-lab certificate are all in original, untouched condition (no scratches, no scuffs on the stone, certificate seal intact).</li>
              <li><b>Return shipping:</b> For an eligible return, we schedule a reverse pickup at our cost within India. For international orders, return shipping is borne by the buyer.</li>
              <li><b>Non-returnable cases:</b> A stone that has been mounted into a ring / pendant, engraved, or worn on the skin is <b>not eligible</b> for return.</li>
              <li><b>Refund timeline:</b> Once the returned stone reaches us and passes inspection (usually 1–2 business days), the refund is initiated. Bank credit typically completes within 5–7 business days depending on your issuing bank.</li>
              <li><b>Wrong / damaged delivery:</b> If you receive a wrong SKU, a damaged stone, or a mismatched certificate, contact us within 48 hours of delivery with clear photographs. We will replace the item at no cost or issue a full refund (buyer's choice).</li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>4. 1:1 Consultations with Human Astrologers</h2>
            <ul className={UL}>
              <li><b>Free reschedule:</b> You may reschedule your session for free up to 12 hours before the confirmed slot. Just message us on Instagram or call the support number below.</li>
              <li><b>Refund before the session:</b> If you cancel more than 12 hours before the session, the full amount is refunded to the wallet within 24 hours or to the original payment method within 5–7 business days.</li>
              <li><b>No-show / late cancellation:</b> If you cancel less than 12 hours before, or do not show up at the scheduled time, the session fee is <b>non-refundable</b> — the astrologer's calendar was blocked for you.</li>
              <li><b>Astrologer-side cancellation:</b> If the astrologer is unable to conduct the session, you will receive a full refund plus a complimentary reschedule at no additional cost.</li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>5. How to Request a Cancellation or Refund</h2>
            <p className={P}>
              Reach us via email or fill out the contact form below with your
              order ID (or Razorpay payment id) and a one-line reason. We reply
              within one business day (Mon–Sat, 10 AM – 8 PM IST).
            </p>
            <div className="rounded-md border border-amber-300 bg-[#FFF6E0] p-4 sm:p-5 mt-4 space-y-2">
              <div className="text-sm text-[#3F2D1B]">
                <Envelope size={14} weight="fill" className="inline mr-2 text-amber-700" />
                Email <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold hover:underline" data-testid="refund-email-link">{SUPPORT_EMAIL}</a>
              </div>
              <div className="text-sm text-[#3F2D1B]">
                Or use our{" "}
                <Link to="/contact" className="font-semibold text-amber-700 hover:underline" data-testid="refund-contact-form-link">
                  Contact Us form &rarr;
                </Link>
              </div>
            </div>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>6. Refund Method &amp; Timelines</h2>
            <ul className={UL}>
              <li><b>Method:</b> Refunds are always processed to the original payment method (Razorpay → your card / UPI / netbanking account). Alternative methods (e.g., wallet credit) are offered only if you explicitly opt in.</li>
              <li><b>Processing:</b> Once approved, refunds are initiated within 24 hours from our side.</li>
              <li><b>Bank credit:</b> Actual credit to your account depends on your issuing bank. UPI refunds are typically instant; card refunds take 3–7 business days; net-banking refunds take 5–7 business days.</li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2 className={H2}>7. Disputes &amp; Chargebacks</h2>
            <p className={P}>
              Before filing a chargeback with your bank, please give us a
              chance to resolve the issue directly. Most refund concerns are
              settled within 24 hours of contact. A chargeback initiated
              before contacting us may delay the resolution as Razorpay
              enters a formal dispute investigation window.
            </p>
          </section>

          <p className="text-xs text-muted mt-10">
            This policy is governed by the laws of India and jurisdiction lies with the courts of New Delhi.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
